import { atom, useAtom } from 'jotai';
import { Intent } from '@teetsh/app/src/components/Buttons/BaseButtonProps';
import { InfoboxLayout } from 'components/Callout/Infobox';

interface NotifPayload {
  timeout?: number;
  intent?: Intent;
  title: string;
  message?: React.ReactNode | string;
  icon?: any;
  open: boolean;
  closable?: boolean;
  actions?: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  layout?: InfoboxLayout;
}

const notifPayload = atom<NotifPayload>({ open: false, title: '' });

export const notifyErrorAtom = atom(
  null,
  (_, set, payload: Omit<NotifPayload, 'open'>) => {
    set(notifPayload, {
      timeout: payload.timeout || 5000,
      title: payload.title,
      message: payload.message,
      intent: 'danger',
      icon: payload?.icon,
      open: true,
      closable: payload?.closable === undefined ? true : payload?.closable,
      actions: payload.actions,
      align: payload?.align,
      layout: payload?.layout,
    });
  },
);

export default function useNotification() {
  const [openState, setIsOpen] = useAtom(notifPayload);
  return {
    isOpen: openState.open,
    title: openState?.title,
    message: openState?.message,
    icon: openState.icon,
    intent: openState.intent,
    timeout: openState.timeout,
    closable: openState.closable,
    actions: openState.actions,
    align: openState.align,
    layout: openState.layout,
    notifyError: (payload: Omit<NotifPayload, 'open'>) =>
      setIsOpen({
        timeout: payload.timeout || 5000,
        title: payload.title,
        message: payload.message,
        intent: 'danger',
        icon: payload?.icon,
        open: true,
        closable: payload?.closable === undefined ? true : payload?.closable,
        actions: payload.actions,
        align: payload?.align,
        layout: payload?.layout,
      }),
    notifySuccess: (payload: Omit<NotifPayload, 'open'>) =>
      setIsOpen({
        timeout: payload.timeout || 5000,
        title: payload.title,
        message: payload.message,
        intent: 'success',
        icon: payload?.icon,
        open: true,
        closable: payload?.closable === undefined ? true : payload?.closable,
        actions: payload.actions,
        align: payload?.align,
        layout: payload?.layout,
      }),
    notifyWarning: (payload: Omit<NotifPayload, 'open'>) =>
      setIsOpen({
        timeout: payload.timeout || 5000,
        title: payload.title,
        message: payload.message,
        intent: 'warning',
        icon: payload?.icon,
        open: true,
        closable: payload?.closable === undefined ? true : payload?.closable,
        actions: payload.actions,
        align: payload?.align,
        layout: payload?.layout,
      }),
    open: (payload: Omit<NotifPayload, 'open'>) =>
      setIsOpen({
        timeout: payload.timeout || 5000,
        title: payload.title,
        message: payload.message,
        intent: payload?.intent || 'primary',
        icon: payload?.icon,
        open: true,
        closable: payload?.closable === undefined ? true : payload?.closable,
        actions: payload.actions,
        align: payload?.align,
        layout: payload?.layout,
      }),
    close: () =>
      setIsOpen({
        ...openState,
        open: false,
      }),
  };
}
